import { Component, Input } from '@angular/core';
import { Invoice } from 'src/app/shared/models/invoice.interface';

/**
 * Export Certificate PDF Format Component
 *
 * This component renders invoices in an export certificate format
 * that displays detailed vehicle export information including:
 * - Certificate details and numbers
 * - Vehicle specifications and classification
 * - Motor vehicle inspection organization details
 * - Owner and user information
 * - Company certification and signatures
 *
 * Supports both individual and merged invoice modes.
 */
@Component({
  selector: 'app-export-certificate',
  templateUrl: './export-certificate.component.html',
  styleUrls: ['./export-certificate.component.scss']
})
export class ExportCertificateComponent {
  @Input() invoices: Invoice[] = [];
  @Input() viewMode: string = 'individual';

  constructor() { }

  formatDate(dateString: string | null): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  formatCurrency(amount: number | null, currency: string | null = 'USD'): string {
    if (!amount || amount === 0) return '';
    const curr = currency || 'USD';
    return `${curr} ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  // Helper method to get vehicle count for display
  getVehicleCount(invoice: any): number {
    if (this.viewMode === 'merged' && invoice.vehicles) {
      return invoice.vehicles.length;
    }
    return 1;
  }

  // Helper method to safely access nested properties
  safeGet(obj: any, path: string, defaultValue: string = ''): string {
    const value = path.split('.').reduce((current, key) => current?.[key], obj);
    return value || defaultValue;
  }

  // Generate certificate number based on invoice
  getCertificateNumber(invoice: any): string {
    return `EXP-${invoice.invoice_number || '000000'}`;
  }

  // Get current date for certificate
  getCurrentDate(): string {
    return new Date().toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  // Get current day for certificate
  getCurrentDay(): string {
    return new Date().getDate().toString().padStart(2, '0');
  }

  // Get current month for certificate
  getCurrentMonth(): string {
    return (new Date().getMonth() + 1).toString().padStart(2, '0');
  }

  // Get current year for certificate
  getCurrentYear(): string {
    return new Date().getFullYear().toString();
  }
}
